import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom-v6'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
} from '@clouway/ui'
import { MapPin, Search, Truck } from 'lucide-react'

import { useDebounce } from '@/hooks/useDebounce'
import { useSearchCustomers } from '../hooks'
import type { Customer } from '../types'

function CustomerCard({ customer }: { customer: Customer }) {
  const { t } = useTranslation('feature')
  const base = `/customers/${customer.id}/tracking-objects`

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{customer.name}</CardTitle>
        <div className="text-muted-foreground flex justify-between text-sm">
          <span>{customer.uid}</span>
          {customer.taxId && <span>{customer.taxId}</span>}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 sm:flex-row">
        <Button asChild variant="outline" className="flex-1">
          <Link to={base}>
            <Truck className="size-4" />
            {t('customer.button.trackingObjects')}
          </Link>
        </Button>
        <Button asChild variant="outline" className="flex-1">
          <Link to={`${base}/activate`}>
            <MapPin className="size-4" />
            {t('customer.button.trackerActivation')}
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

export function CustomersSearch() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  // The URL is the source of truth so the query survives navigation (plan.md
  // §3.2 — replaces the mobile appState).
  const query = searchParams.get('q') ?? ''
  const debouncedQuery = useDebounce(query, 500)
  const { data: customers, isSuccess, isFetching } = useSearchCustomers(debouncedQuery)

  const onChange = (value: string) => {
    setSearchParams(value ? { q: value } : {}, { replace: true })
  }

  const showResults = isSuccess && !isFetching && customers !== undefined

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <Card>
        <CardContent className="pt-6">
          <InputGroup>
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              value={query}
              placeholder={t('ui.label.search')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
              aria-label={t('ui.label.search')}
            />
          </InputGroup>
          {showResults && (
            <p className="text-muted-foreground mt-3 text-center text-sm">
              {t('search.results')}
              <span className="italic underline">{query}</span>
              <span className="text-foreground font-bold">: {customers.length}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {isFetching && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      )}

      {showResults &&
        customers.map(customer => <CustomerCard key={customer.id} customer={customer} />)}
    </div>
  )
}
